/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { PRODUCT_TYPE } from 'configs/constants';
import monitoringService from 'infrastructure/serviceHandlers/Monitoring/Monitoring';
import {
  getAllDeviceInSpace,
  getDeletedDevicesInSpace
} from 'services/api/devicesApi';
import {
  TDeletedDeviceType,
  TDeviceResponseType,
  TSpaceDevicesState
} from 'typings';

export const fetchSpaceDevices = createAsyncThunk(
  'spaces/fetchSpaceDevices',
  async (payload: string, thunkAPI) => {
    try {
      const response = await getAllDeviceInSpace(payload);
      console.log('all devices in space Response', response.data);
      return response.data.items as TDeviceResponseType[];
    } catch (error) {
      monitoringService.captureException(error, {
        tags: {
          feature: 'fetchSpaceDevices',
          error_type: 'api_error'
        },
        extra: {
          payload
        }
      });
      return thunkAPI.rejectWithValue('failed');
    }
  }
);

// Deleted devices only tell us whether this user has paired before; they are
// never added to the live count. Failure is non-fatal: the caller falls back
// to the live-devices-only heuristic, so a missing endpoint degrades to the
// pre-existing behaviour rather than blocking the pairing screen.
export const fetchDeletedSpaceDevices = createAsyncThunk(
  'spaces/fetchDeletedSpaceDevices',
  async (spaceId: string, thunkAPI) => {
    try {
      const response = await getDeletedDevicesInSpace(spaceId);
      return (response.data.items ?? []) as TDeletedDeviceType[];
    } catch (error) {
      monitoringService.captureException(error, {
        tags: {
          feature: 'fetchDeletedSpaceDevices',
          error_type: 'api_error'
        },
        extra: { spaceId }
      });
      return thunkAPI.rejectWithValue('failed');
    }
  }
);

const initialSpaceDevicesState: TSpaceDevicesState = {
  spaceDevices: [],
  deletedDevices: [],
  is4gHomeCameraAvailable: false,
  isLoading: false,
  isDeletedDevicesLoading: false
};

export const spaceDevicesSlice = createSlice({
  name: 'spaceDevices',
  initialState: initialSpaceDevicesState,
  reducers: {
    clearSpaceDevices: (state) => {
      state.spaceDevices = [];
      state.deletedDevices = [];
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSpaceDevices.fulfilled, (state, action) => {
        state.isLoading = false;
        state.spaceDevices = action.payload;
        const is4gHomeCameraAvailable = action.payload.find(
          (dvc) =>
            dvc?.four_g && dvc.model_product_type === PRODUCT_TYPE.HOME_SECURITY
        );
        state.is4gHomeCameraAvailable = Boolean(is4gHomeCameraAvailable?.id);
      })
      .addCase(fetchSpaceDevices.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchSpaceDevices.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(fetchDeletedSpaceDevices.fulfilled, (state, action) => {
        state.isDeletedDevicesLoading = false;
        state.deletedDevices = action.payload;
      })
      .addCase(fetchDeletedSpaceDevices.pending, (state) => {
        state.isDeletedDevicesLoading = true;
      })
      .addCase(fetchDeletedSpaceDevices.rejected, (state) => {
        state.isDeletedDevicesLoading = false;
        state.deletedDevices = [];
      });
  }
});

// Action creators are generated for each case reducer function
export const { clearSpaceDevices } = spaceDevicesSlice.actions;

export default spaceDevicesSlice.reducer;
